import React, { ReactNode, useState } from 'react';
import { Icon, UserAvatar, Title, Link } from '@common/components';
import { Colors, FontSize, LineHeight } from '@common/tokens';
import { Divider, Menu } from 'antd';
import { useLocation } from '@common/hooks';
import { TermsAndConditionsLink } from '@identity/pages/account-settings/terms-and-conditions';
import { useCurrentMembership, useCurrentEntity } from '@identity/hooks';

const accountSettingsMenuItems: {
  [key: string]: {
    key: string;
    label: string;
    route: string;
    icon: ReactNode;
  };
} = {
  userSettings: {
    key: 'user-settings',
    label: 'User Settings',
    route: '/works/account-settings/user-settings',
    icon: <Icon icon="userGear" kind="light" size="xl" />,
  },
  changePassword: {
    key: 'change-password',
    label: 'Login & Password',
    route: '/works/account-settings/change-password',
    icon: <Icon icon="lockKeyhole" kind="light" size="xl" />,
  },
  notifications: {
    key: 'notifications',
    label: 'Notifications',
    route: '/works/account-settings/notifications',
    icon: <Icon icon="envelopes" kind="light" size="xl" />,
  },
};

function getActiveMenuKey(currentRoute?: string) {
  const objectKey =
    Object.keys(accountSettingsMenuItems).find((key) => {
      const item = accountSettingsMenuItems[key];
      return currentRoute?.includes(item.route);
    }) || '';

  return accountSettingsMenuItems[objectKey]?.key;
}

export const AccountSettingsMenu = () => {
  const currentMembership = useCurrentMembership();
  const currentEntity = useCurrentEntity();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(
    getActiveMenuKey(location.pathname)
  );

  const canShowTermsAndConditionsLink =
    currentMembership.canShowTermsConditionsLink;

  return (
    <>
      <section className="account-settings__profile">
        <div className="account-settings__profile-name">
          <>
            <UserAvatar
              backgroundColor={Colors.elmDarkest}
              initials={currentMembership.worksUser.initials}
              style={{ marginRight: 12 }}
            />
            <Title size="h2" className="sentry-mask">
              {currentMembership.name}
            </Title>
          </>
        </div>
        <ul className="account-settings__profile-info">
          <li>
            <span>
              <Icon icon="building" kind="light" />
            </span>
            {currentEntity.name}
          </li>
          <li>
            <span>
              <Icon icon="user" kind="light" />
            </span>
            {currentMembership.roleName}
          </li>
          <li className="sentry-mask">
            <span>
              <Icon icon="envelope" kind="light" />
            </span>
            {currentMembership.worksUser.email}
          </li>
        </ul>
        <Divider />
        <Menu
          className="account-settings__profile-menu"
          mode="vertical"
          defaultSelectedKeys={[accountSettingsMenuItems.changePassword.key]}
          selectedKeys={[activeMenu]}
          onSelect={(e) => setActiveMenu(e.key)}
        >
          {Object.keys(accountSettingsMenuItems).map((item) => {
            return (
              <Menu.Item
                className="account-settings__profile-menu-item"
                key={accountSettingsMenuItems[item].key}
                icon={
                  <span className="account-settings__icon">
                    {accountSettingsMenuItems[item].icon}
                  </span>
                }
              >
                <Link
                  className="account-settings__profile-menu-item__link"
                  to={accountSettingsMenuItems[item].route}
                >
                  {accountSettingsMenuItems[item].label}
                  <Icon icon="angleRight" kind="light" />
                </Link>
              </Menu.Item>
            );
          })}
        </Menu>
        {canShowTermsAndConditionsLink && <TermsAndConditionsLink />}
      </section>
      <style jsx>{`
        .account-settings__profile {
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 4px 8px 0 rgba(36, 35, 41, 0.08);
        }

        .account-settings__profile-name {
          display: flex;
        }

        .account-settings__profile-name :global(.ant-typography) {
          margin: 0;
        }

        .account-settings__icon {
          width: 24px;
          height: 24px;
          display: flex;
          margin-right: 16px;
          align-items: center;
        }

        .account-settings__profile-info {
          margin: 24px 0;
          list-style-type: none;
          padding: 0;
          line-height: normal;
        }

        .account-settings__profile-info li {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
        }

        .account-settings__profile-info li > span {
          display: flex;
          align-items: center;
          margin-right: 8px;
          width: 20px;
        }

        .account-settings__profile-menu-item__link {
          font-size: ${FontSize.md};
          line-height: ${LineHeight.sm};
        }
      `}</style>
    </>
  );
};
